import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import './configurations.css'
import Style from './configurationsPage.module.css'
import ConnectorIcon from '../../SharedComponents/ConnectorIcon/ConnectorIcon'
import PageLoader from '../../SharedComponents/PageState/PageLoader'
import PageNotice from '../../SharedComponents/PageState/PageNotice'
import { describeConnection, useGetConnectionInstanceQuery } from '../../../features/connections/connectionsApi'
import { useGetConfigurationsQuery, useUpdateConfigurationMutation } from '../../../features/configurations/configurationsApi'
import { CLIENT_PERMISSION } from '../../../features/clientUsers/clientAccess'
import { useSubscriptionAccess } from '../../../features/clientUsers/useClientAccess'
import { PATH, paths } from '../../../app/routes'
const SUCCESS_BANNER_MS = 5000
const SECRET_KEY = /password|secret|token|apikey|api_key/i

const humanize = (key) => String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (letter) => letter.toUpperCase())

const mask = (value) => (value ? '•'.repeat(Math.min(String(value).length, 24)) : '')

const ConfigurationRow = ({ item, isSaving, canEdit, canReveal, onSave }) => {
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState(item.values)
    const [isRevealed, setIsRevealed] = useState(false)
    const isSecret = SECRET_KEY.test(item.key)
    const value = String(item.values || '').trim()
    const inputId = `config-${item.id}`

    useEffect(() => {
        if (!isEditing) {
            setDraft(item.values)
        }
    }, [item.values, isEditing])

    const cancel = () => setIsEditing(false)
    const save = () => onSave({ ...item, values: draft }).then((saved) => {
        if (saved) {
            setIsEditing(false)
        }
    })
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') save()
        if (event.key === 'Escape') cancel()
    }

    return (
        <li className={[Style.row, isEditing ? Style.rowEditing : ''].join(' ')}>
            <div className={Style.keyCell}>
                <label htmlFor={inputId} className={Style.label}>{humanize(item.key)}</label>
                <code className={Style.key}>{item.key}</code>
            </div>
            <div className={Style.valueCell}>
                {isEditing ? (
                    <input
                        id={inputId}
                        className={`form-control ${Style.input}`}
                        type={isSecret && !isRevealed ? 'password' : 'text'}
                        value={draft || ''}
                        autoFocus
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                ) : (
                    <span className={[Style.value, value ? '' : Style.valueEmpty].join(' ')} title={isSecret && !isRevealed ? undefined : value}>
                        {value ? (isSecret && !isRevealed ? mask(value) : value) : 'Not set'}
                    </span>
                )}
                {canReveal && isSecret && (value || isEditing) ? (
                    <button
                        type="button"
                        className={Style.iconButton}
                        aria-label={isRevealed ? 'Hide value' : 'Show value'}
                        onClick={() => setIsRevealed(!isRevealed)}
                    >
                        <i className={isRevealed ? 'uil-eye-slash' : 'uil-eye'} />
                    </button>
                ) : null}
            </div>
            <div className={Style.actions}>
                {isEditing ? (
                    <>
                        <button type="button" className={Style.secondary} disabled={isSaving} onClick={cancel}>Cancel</button>
                        <button type="button" className={Style.primary} disabled={isSaving} onClick={save}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </>
                ) : canEdit ? (
                    <button type="button" className={Style.secondary} aria-label={`Edit ${humanize(item.key)}`} onClick={() => setIsEditing(true)}>
                        <i className="uil-pen" aria-hidden="true" /> Edit
                    </button>
                ) : null}
            </div>
        </li>
    )
}

const matchesEndpoint = (items, endpoint) => {
    const name = items[0] && items[0].endpointName
    return Boolean(name) && (endpoint.name === name || endpoint.shortName === name)
}

const groupByEndpoint = (connection, { source, target }) => {
    const lists = [source, target]
    const endpoints = [connection.source, connection.target]
    const claimed = new Set()
    const owner = endpoints.map((endpoint) => {
        const index = lists.findIndex((items, i) => !claimed.has(i) && matchesEndpoint(items, endpoint))
        if (index !== -1) claimed.add(index)
        return index
    })
    return endpoints.map((endpoint, i) => {
        let index = owner[i]
        if (index === -1) {
            index = [i, 1 - i].find((candidate) => !claimed.has(candidate))
            claimed.add(index)
        }
        return { key: i === 0 ? 'source' : 'target', endpoint, items: lists[index] || [] }
    })
}

const ConfigurationSection =({ endpoint, items, savingId, canEdit, canReveal, onSave }) => (
    <section className={Style.section}>
        <header className={Style.sectionHeader}>
            <span className={Style.sectionIcon}>
                <ConnectorIcon shortName={endpoint.shortName} fallbackClassName={Style.sectionFallback} decorative />
            </span>
            <h2 className={Style.sectionTitle}>{endpoint.name}</h2>
            <span className={Style.count}>{items.length}</span>
        </header>
        {items.length > 0 ? (
            <ul className={Style.list}>
                {items.map((item) => (
                    <ConfigurationRow key={item.id} item={item} isSaving={savingId === item.id} canEdit={canEdit} canReveal={canReveal} onSave={onSave} />
                ))}
            </ul>
        ) : (
            <p className={Style.sectionEmpty}>No configurations for {endpoint.name}.</p>
        )}
    </section>
)

const Configurations = () => {
    const { connectorId, instanceId } = useParams()
    const connectionQuery = useGetConnectionInstanceQuery({ connectorId, instanceId })
    const configurationQuery = useGetConfigurationsQuery({ connectorId, instanceId })
    const [updateConfiguration] = useUpdateConfigurationMutation()
    const { can } = useSubscriptionAccess(connectionQuery.data && connectionQuery.data.subscriptionId)
    const [savingId, setSavingId] = useState(null)
    const [banner, setBanner] = useState(null)

    useEffect(() => {
        if (!banner || banner.tone !== 'success') {
            return undefined
        }
        const timer = setTimeout(() => setBanner(null), SUCCESS_BANNER_MS)
        return () => clearTimeout(timer)
    }, [banner])

    if (connectionQuery.isLoading || configurationQuery.isLoading) {
        return <PageLoader message="Loading configurations..." />
    }
    const connection = connectionQuery.data
    if (connectionQuery.isError || configurationQuery.isError || !connection) {
        return (
            <PageNotice
                title="Configurations could not be loaded"
                hint="The connector may have been removed, or the link is incomplete."
                linkTo={PATH.MY_PRODUCTS}
                linkLabel="Go to My Products"
            />
        )
    }
    const save = (item) => {
        setBanner(null)
        setSavingId(item.id)
        return updateConfiguration({
            connectorId,
            instanceId,
            sourceEndpointId: connection.source.id,
            targetEndpointId: connection.target.id,
            item
        })
            .unwrap()
            .then((result) => {
                const saved = result === true
                setBanner(saved
                    ? { tone: 'success', message: `${humanize(item.key)} saved.` }
                    : { tone: 'danger', message: `${humanize(item.key)} was not saved.` })
                return saved
            })
            .catch(() => {
                setBanner({ tone: 'danger', message: `${humanize(item.key)} could not be saved. Please try again.` })
                return false
            })
            .finally(() => setSavingId(null))
    }

    const connector = describeConnection(connection)
    const title = connector.displayName || `${connector.source.name} ⇄ ${connector.target.name}`
    const sections = groupByEndpoint(connection, configurationQuery.data)

    return (
        <div className="main-content mn-dashboard">
            <div className="page-content custompaddingdash">
                <div className={`container-fluid ${Style.page}`}>
                    <header className={Style.header}>
                        <ol className={`breadcrumb ${Style.breadcrumb}`}>
                            <li className="breadcrumb-item"><Link to={PATH.DASHBOARD}>Dashboard</Link></li>
                            <li className="breadcrumb-item"><Link to={paths.productDetail(connectorId)}>{title}</Link></li>
                            <li className="breadcrumb-item active" aria-current="page">Configurations</li>
                        </ol>
                        <h1 className={Style.title}>Configurations</h1>
                    </header>
                    {banner ? (
                        <div className={[Style.banner, banner.tone === 'success' ? Style.bannerSuccess : Style.bannerError].join(' ')} role={banner.tone === 'success' ? 'status' : 'alert'}>
                            <i className={banner.tone === 'success' ? 'uil-check-circle' : 'uil-exclamation-circle'} aria-hidden="true" />
                            {banner.message}
                        </div>
                    ) : null}
                    {sections.map((section) => (
                        <ConfigurationSection
                            key={section.key}
                            endpoint={section.endpoint}
                            items={section.items}
                            savingId={savingId}
                            canEdit={can(CLIENT_PERMISSION.CONFIG_WRITE)}
                            canReveal={can(CLIENT_PERMISSION.CONFIG_REVEAL_SECRETS)}
                            onSave={save}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}

export default Configurations
